import { useState } from "react";
import { ApiError } from "../../api/client";
import type { Authorization, CounterPrinted, Courier, Settlement, Statement } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { formatTime } from "../../lib/time";
import { Dialog } from "../../ui/Dialog";
import { methodsOf } from "./CourierBon";

interface Props {
  statement: Statement;
  /** The couriers a delivery can go to instead ("Andere koerier"). */
  couriers: readonly Courier[];
  /** Who gave their code ("Afgerekend door"). */
  authorization: Authorization;
  /** Settled, and where the afrekenbon was printed (null: no printer, the browser prints the copy). */
  onSettled: (settlement: Settlement, printed: CounterPrinted) => void;
  /** A delivery went to another courier: the board reloads. */
  onMoved: (message: string) => void;
  onClose: () => void;
}

/**
 * "Afrekenen · Mehmet" (prototype openSettle), after the code of a manager or the owner: every
 * delivery on the afrekenbon, the rides, the cash to receive and what was paid with the bank-QR or
 * beforehand. No money is counted (kassa-besluiten): the afrekenbon is printed and kept. A delivery
 * at the wrong courier goes to another one from here, with the same code (Furkan, 11/10/2026).
 */
export function SettleDialog({ statement: first, couriers, authorization, onSettled, onMoved, onClose }: Props) {
  const { api } = useTill();
  const text = t.couriers.settleDialog;
  const [statement, setStatement] = useState(first);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const others = couriers.filter((courier) => courier.id !== statement.courier.id);

  const move = async (orderId: string, code: string, courier: Courier) => {
    setBusy(true);
    setError(null);
    try {
      await api.request("POST", `/device/deliveries/${orderId}/courier`, {
        body: { courierId: courier.id },
        staffToken: authorization.token,
      });
      const answer = await api.request<{ statement: Statement }>(
        "GET",
        `/device/couriers/${statement.courier.id}/statement`,
        { retry: true },
      );
      onMoved(text.moved(code, courier.name));
      if (answer.statement.rides === 0) {
        onMoved(text.nothingLeft(statement.courier.name));
        onClose();
        return;
      }
      setStatement(answer.statement);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t.errors.unexpected);
    }
    setBusy(false);
  };

  const settle = async () => {
    setBusy(true);
    setError(null);
    try {
      const answer = await api.request<{ settlement: Settlement; printed: CounterPrinted }>(
        "POST",
        `/device/couriers/${statement.courier.id}/settle`,
        { body: { orderIds: statement.orderIds }, staffToken: authorization.token },
      );
      onSettled(answer.settlement, answer.printed);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : t.errors.unexpected);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={text.title(statement.courier.name)}
      onClose={onClose}
      footer={
        <>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {text.cancel}
          </button>
          <button type="button" className="btn primary" disabled={busy} onClick={() => void settle()}>
            {text.confirm}
          </button>
        </>
      }
    >
      <div className="tablewrap">
        <table className="settletbl stack">
          <thead>
            <tr>
              <th>{text.columns.code}</th>
              <th>{text.columns.delivered}</th>
              <th>{text.columns.address}</th>
              <th>{text.columns.payment}</th>
              <th className="r">{text.columns.amount}</th>
              {others.length > 0 && <th>{text.columns.courier}</th>}
            </tr>
          </thead>
          <tbody>
            {statement.lines.map((line, index) => {
              // a delivery paid in two ways has two lines: the choice once, on its first
              const firstLine = statement.lines.findIndex((item) => item.orderId === line.orderId) === index;
              return (
                <tr key={`${line.orderId}-${line.group}`}>
                  <td className="code full">{line.code}</td>
                  <td className="num" data-label={text.columns.delivered}>
                    {formatTime(line.deliveredAt)}
                  </td>
                  <td className="grow" data-label={text.columns.address}>
                    {line.address}
                  </td>
                  <td data-label={text.columns.payment}>{methodsOf(line)}</td>
                  <td className="r num" data-label={text.columns.amount}>
                    {formatEuro(line.amountCents)}
                  </td>
                  {others.length > 0 && (
                    <td data-label={text.columns.courier}>
                      {firstLine && (
                        <select
                          aria-label={text.otherCourier(line.code)}
                          value=""
                          disabled={busy}
                          onChange={(event) => {
                            const courier = others.find((item) => item.id === event.target.value);
                            if (courier) void move(line.orderId, line.code, courier);
                          }}
                        >
                          <option value="">{statement.courier.name}</option>
                          {others.map((courier) => (
                            <option key={courier.id} value={courier.id}>
                              → {courier.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="kvs">
        <div>
          <span>{text.rides}</span>
          <b>{statement.rides}</b>
        </div>
        <div>
          <span>{text.cash}</span>
          <b>{formatEuro(statement.cashCents)}</b>
        </div>
        <div>
          <span>{text.other}</span>
          <b>{formatEuro(statement.qrCents + statement.prepaidCents)}</b>
        </div>
      </div>
      {others.length > 0 && <p className="note">{text.moveNote}</p>}
      <p className="note">
        {text.by} <b>{authorization.staff.name}</b>
        {text.note(formatEuro(statement.cashCents))}
      </p>
      {error !== null && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}
